import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorCode, isRequestOutcomeUnknown, newIdempotencyKey } from '../utils/collectionReminders';

/**
 * Clave de idempotencia de un formulario de envío (programar o "enviar ahora").
 *
 * Una clave por INTENCIÓN: se genera al abrir y se reutiliza al reintentar el mismo envío,
 * para que billing devuelva lo ya creado si la primera respuesta se perdió.
 *
 * Después de un error:
 *   · si billing lo rechazó (4xx o con código), no guardó nada con esa clave: si el operador
 *     edita el formulario, es una intención nueva y lleva clave nueva;
 *   · si no se sabe (sin respuesta, 5xx), la clave se conserva aunque edite. Si el primer
 *     intento sí se guardó, billing responde 409 `idempotency_key_reused` en vez de mezclar
 *     los datos nuevos con los guardados; con eso se avisa y recién ahí se cambia la clave.
 *
 * @param {boolean} open cada apertura genera una clave nueva
 * @returns {{ key: string|null, keyError: boolean,
 *   onRequestFailed: (err: unknown) => void, onEdited: () => void }}
 */
export default function useIdempotencyKey(open) {
  const [key, setKey] = useState(null);
  const [keyError, setKeyError] = useState(false);
  // Último intento fallido con la clave vigente: null, 'rejected' o 'unknown'. 'unknown' no
  // vuelve atrás: un rechazo posterior no prueba que el primer intento no se guardó.
  const failure = useRef(null);

  const renew = useCallback(() => {
    failure.current = null;
    try {
      setKey(newIdempotencyKey());
      setKeyError(false);
    } catch {
      // Sin generador aleatorio no hay clave segura: el formulario lo avisa y no envía.
      setKey(null);
      setKeyError(true);
    }
  }, []);

  useEffect(() => {
    if (open) renew();
  }, [open, renew]);

  const onRequestFailed = useCallback(
    (err) => {
      if (apiErrorCode(err) === 'idempotency_key_reused') {
        renew();
        return;
      }
      if (failure.current !== 'unknown') {
        failure.current = isRequestOutcomeUnknown(err) ? 'unknown' : 'rejected';
      }
    },
    [renew],
  );

  const onEdited = useCallback(() => {
    if (failure.current === 'rejected') renew();
  }, [renew]);

  return { key, keyError, onRequestFailed, onEdited };
}
